export type DataAccessLevel = "all" | "region" | "team" | "own_team" | "own" | "view";

export type ModulePermission = {
  view: boolean;
  create: boolean;
  edit: boolean;
  delete_: boolean;
};

export type PositionModuleKey =
  | "Leads"
  | "Pipeline"
  | "Quotations"
  | "Projects"
  | "Lead Import"
  | "Calls"
  | "WhatsApp"
  | "Campaigns"
  | "Activity Feed"
  | "Reports"
  | "Integrations"
  | "Web Forms"
  | "Users"
  | "Positions & Roles"
  | "Team Structure"
  | "Industry Config"
  | "Project Settings"
  | "Assignment Rules"
  | "Lead Scoring"
  | "Workflows"
  | "Bot Identity"
  | "Knowledge Base";

export interface Position {
  id: string;
  name: string;
  level: number;
  reportsTo: string;
  color: string;
  dataAccess: DataAccessLevel;
  userCount: number;
  permissions: Record<PositionModuleKey, ModulePermission>;
  createdAt: string;
}

export const dataAccessOptions: { value: DataAccessLevel; label: string; description: string }[] = [
  {
    value: "own",
    label: "Own Data Only",
    description: "Can only see leads and data assigned directly to them. Cannot see teammates' data.",
  },
  {
    value: "own_team",
    label: "Own + Team View",
    description: "Can see own data plus view (read-only) their direct team members' data. Cannot edit others.",
  },
  {
    value: "team",
    label: "Team Data Only",
    description: "Can see and manage all data belonging to their direct team. Cannot see other teams' data.",
  },
  {
    value: "region",
    label: "Department / Region Data",
    description: "Can see all data in their department or region across multiple teams.",
  },
  {
    value: "all",
    label: "All Company Data",
    description: "Full visibility across entire company. Recommended only for top-level positions.",
  },
];

export const positionColorOptions = [
  { value: "#8b5cf6", name: "Purple" },
  { value: "#4f46e5", name: "Indigo" },
  { value: "#3b82f6", name: "Blue" },
  { value: "#14b8a6", name: "Teal" },
  { value: "#22c55e", name: "Green" },
  { value: "#f59e0b", name: "Amber" },
  { value: "#f97316", name: "Orange" },
  { value: "#ef4444", name: "Red" },
];

export const dataAccessBadgeColors: Record<DataAccessLevel, string> = {
  all: "bg-purple-50 text-purple-700",
  region: "bg-blue-50 text-blue-700",
  team: "bg-teal-50 text-teal-700",
  own_team: "bg-green-50 text-green-700",
  own: "bg-gray-100 text-gray-600",
  view: "bg-gray-100 text-gray-500",
};

export const levelBadgeColors: Record<number, string> = {
  1: "bg-purple-100 text-purple-700",
  2: "bg-indigo-100 text-indigo-700",
  3: "bg-blue-100 text-blue-700",
  4: "bg-teal-100 text-teal-700",
  5: "bg-green-100 text-green-700",
  6: "bg-amber-100 text-amber-700",
  7: "bg-orange-100 text-orange-700",
  8: "bg-gray-100 text-gray-600",
  9: "bg-gray-100 text-gray-500",
  10: "bg-gray-100 text-gray-500",
};

export function createEmptyPermissions(): Record<PositionModuleKey, ModulePermission> {
  const keys: PositionModuleKey[] = [
    "Leads", "Pipeline", "Quotations", "Projects", "Lead Import",
    "Calls", "WhatsApp", "Campaigns", "Activity Feed",
    "Reports", "Integrations", "Web Forms",
    "Users", "Positions & Roles", "Team Structure", "Industry Config",
    "Project Settings", "Assignment Rules", "Lead Scoring", "Workflows",
    "Bot Identity", "Knowledge Base",
  ];
  const perms: Record<string, ModulePermission> = {};
  keys.forEach((k) => {
    perms[k] = { view: false, create: false, edit: false, delete_: false };
  });
  return perms as Record<PositionModuleKey, ModulePermission>;
}

export const positionColors: Record<string, string> = {
  "#8b5cf6": "bg-purple-100 text-purple-700",
  "#4f46e5": "bg-indigo-100 text-indigo-700",
  "#3b82f6": "bg-blue-100 text-blue-700",
  "#14b8a6": "bg-teal-100 text-teal-700",
  "#22c55e": "bg-green-100 text-green-700",
  "#f59e0b": "bg-amber-100 text-amber-700",
  "#f97316": "bg-orange-100 text-orange-700",
  "#ef4444": "bg-red-100 text-red-700",
};

export const positionColorDotBg: Record<string, string> = {
  "#8b5cf6": "bg-purple-500",
  "#4f46e5": "bg-indigo-500",
  "#3b82f6": "bg-blue-500",
  "#14b8a6": "bg-teal-500",
  "#22c55e": "bg-green-500",
  "#f59e0b": "bg-amber-500",
  "#f97316": "bg-orange-500",
  "#ef4444": "bg-red-500",
};

export const positions: Position[] = [
  {
    id: "pos-1",
    name: "Owner / CEO",
    level: 1,
    reportsTo: "",
    color: "#8b5cf6",
    dataAccess: "all",
    userCount: 1,
    permissions: createEmptyPermissions(),
    createdAt: "2025-01-15",
  },
  {
    id: "pos-2",
    name: "Sales Director",
    level: 2,
    reportsTo: "Owner / CEO",
    color: "#4f46e5",
    dataAccess: "all",
    userCount: 1,
    permissions: createEmptyPermissions(),
    createdAt: "2025-01-15",
  },
  {
    id: "pos-3",
    name: "Regional Manager",
    level: 3,
    reportsTo: "Sales Director",
    color: "#3b82f6",
    dataAccess: "region",
    userCount: 2,
    permissions: createEmptyPermissions(),
    createdAt: "2025-01-15",
  },
  {
    id: "pos-4",
    name: "Team Leader",
    level: 4,
    reportsTo: "Regional Manager",
    color: "#14b8a6",
    dataAccess: "team",
    userCount: 3,
    permissions: createEmptyPermissions(),
    createdAt: "2025-02-01",
  },
  {
    id: "pos-5",
    name: "Senior Sales Agent",
    level: 5,
    reportsTo: "Team Leader",
    color: "#22c55e",
    dataAccess: "own_team",
    userCount: 4,
    permissions: createEmptyPermissions(),
    createdAt: "2025-02-01",
  },
  {
    id: "pos-6",
    name: "Sales Agent",
    level: 5,
    reportsTo: "Team Leader",
    color: "#22c55e",
    dataAccess: "own",
    userCount: 3,
    permissions: createEmptyPermissions(),
    createdAt: "2025-02-01",
  },
  {
    id: "pos-7",
    name: "Support Executive",
    level: 6,
    reportsTo: "Team Leader",
    color: "#f59e0b",
    dataAccess: "own",
    userCount: 2,
    permissions: createEmptyPermissions(),
    createdAt: "2025-03-10",
  },
  {
    id: "pos-8",
    name: "Viewer / Intern",
    level: 6,
    reportsTo: "Sales Agent",
    color: "#64748b",
    dataAccess: "view",
    userCount: 1,
    permissions: createEmptyPermissions(),
    createdAt: "2025-04-01",
  },
];

// Pre-fill permissions for each position
(function initPermissions() {
  // Owner/CEO: ALL checked everywhere
  const allKeys = Object.keys(positions[0].permissions) as PositionModuleKey[];
  allKeys.forEach((k) => {
    positions[0].permissions[k] = { view: true, create: true, edit: true, delete_: true };
  });

  // Sales Director
  const dirFull = ["Leads", "Pipeline", "Quotations", "Projects", "Calls", "WhatsApp", "Campaigns", "Activity Feed", "Reports", "Integrations", "Web Forms"] as PositionModuleKey[];
  dirFull.forEach((k) => { positions[1].permissions[k] = { view: true, create: true, edit: true, delete_: true }; });
  // Settings: view + edit, no delete
  const dirSettings = ["Users", "Positions & Roles", "Team Structure", "Industry Config", "Project Settings", "Assignment Rules", "Lead Scoring", "Workflows", "Bot Identity", "Knowledge Base"] as PositionModuleKey[];
  dirSettings.forEach((k) => { positions[1].permissions[k] = { view: true, create: true, edit: true, delete_: false }; });

  // Regional Manager
  ["Leads", "Pipeline", "Quotations"].forEach((k) => { positions[2].permissions[k as PositionModuleKey] = { view: true, create: true, edit: true, delete_: false }; });
  positions[2].permissions["Reports"] = { view: true, create: false, edit: false, delete_: false };
  positions[2].permissions["Campaigns"] = { view: true, create: true, edit: false, delete_: false };
  ["Calls", "WhatsApp", "Activity Feed"].forEach((k) => { positions[2].permissions[k as PositionModuleKey] = { view: true, create: true, edit: true, delete_: false }; });
  // Settings: view only
  ["Users", "Positions & Roles", "Team Structure", "Industry Config", "Project Settings", "Assignment Rules", "Lead Scoring", "Workflows", "Bot Identity", "Knowledge Base"].forEach((k) => { positions[2].permissions[k as PositionModuleKey] = { view: true, create: false, edit: false, delete_: false }; });

  // Team Leader
  ["Leads"].forEach((k) => { positions[3].permissions[k as PositionModuleKey] = { view: true, create: true, edit: true, delete_: false }; });
  ["Pipeline"].forEach((k) => { positions[3].permissions[k as PositionModuleKey] = { view: true, create: false, edit: true, delete_: false }; });
  ["Calls", "WhatsApp"].forEach((k) => { positions[3].permissions[k as PositionModuleKey] = { view: true, create: true, edit: true, delete_: false }; });
  positions[3].permissions["Reports"] = { view: true, create: false, edit: false, delete_: false };
  ["Activity Feed"].forEach((k) => { positions[3].permissions[k as PositionModuleKey] = { view: true, create: false, edit: false, delete_: false }; });
  // Settings: view only
  ["Users", "Positions & Roles", "Team Structure", "Industry Config", "Project Settings", "Assignment Rules", "Lead Scoring", "Workflows", "Bot Identity", "Knowledge Base"].forEach((k) => { positions[3].permissions[k as PositionModuleKey] = { view: true, create: false, edit: false, delete_: false }; });

  // Senior Sales Agent
  ["Leads"].forEach((k) => { positions[4].permissions[k as PositionModuleKey] = { view: true, create: true, edit: true, delete_: false }; });
  ["Pipeline"].forEach((k) => { positions[4].permissions[k as PositionModuleKey] = { view: true, create: false, edit: true, delete_: false }; });
  ["Calls"].forEach((k) => { positions[4].permissions[k as PositionModuleKey] = { view: true, create: true, edit: false, delete_: false }; });
  ["WhatsApp"].forEach((k) => { positions[4].permissions[k as PositionModuleKey] = { view: true, create: true, edit: false, delete_: false }; });
  ["Quotations"].forEach((k) => { positions[4].permissions[k as PositionModuleKey] = { view: true, create: true, edit: false, delete_: false }; });
  positions[4].permissions["Reports"] = { view: true, create: false, edit: false, delete_: false };
  ["Activity Feed"].forEach((k) => { positions[4].permissions[k as PositionModuleKey] = { view: true, create: false, edit: false, delete_: false }; });

  // Sales Agent
  ["Leads"].forEach((k) => { positions[5].permissions[k as PositionModuleKey] = { view: true, create: true, edit: true, delete_: false }; });
  ["Pipeline"].forEach((k) => { positions[5].permissions[k as PositionModuleKey] = { view: true, create: false, edit: true, delete_: false }; });
  ["Calls"].forEach((k) => { positions[5].permissions[k as PositionModuleKey] = { view: true, create: true, edit: false, delete_: false }; });
  ["WhatsApp"].forEach((k) => { positions[5].permissions[k as PositionModuleKey] = { view: true, create: true, edit: false, delete_: false }; });
  ["Quotations"].forEach((k) => { positions[5].permissions[k as PositionModuleKey] = { view: true, create: true, edit: false, delete_: false }; });
  positions[5].permissions["Reports"] = { view: true, create: false, edit: false, delete_: false };

  // Support Executive
  ["Leads"].forEach((k) => { positions[6].permissions[k as PositionModuleKey] = { view: true, create: false, edit: true, delete_: false }; });
  ["Pipeline"].forEach((k) => { positions[6].permissions[k as PositionModuleKey] = { view: true, create: false, edit: false, delete_: false }; });
  ["Calls"].forEach((k) => { positions[6].permissions[k as PositionModuleKey] = { view: true, create: true, edit: true, delete_: false }; });
  ["WhatsApp"].forEach((k) => { positions[6].permissions[k as PositionModuleKey] = { view: true, create: true, edit: false, delete_: false }; });
  ["Activity Feed"].forEach((k) => { positions[6].permissions[k as PositionModuleKey] = { view: true, create: false, edit: false, delete_: false }; });

  // Viewer / Intern
  ["Leads"].forEach((k) => { positions[7].permissions[k as PositionModuleKey] = { view: true, create: false, edit: false, delete_: false }; });
  ["Pipeline"].forEach((k) => { positions[7].permissions[k as PositionModuleKey] = { view: true, create: false, edit: false, delete_: false }; });
  ["Reports"].forEach((k) => { positions[7].permissions[k as PositionModuleKey] = { view: true, create: false, edit: false, delete_: false }; });
})();

export const moduleSections: { name: string; modules: PositionModuleKey[] }[] = [
  {
    name: "Leads & Clients",
    modules: ["Leads", "Pipeline", "Quotations", "Projects", "Lead Import"],
  },
  {
    name: "Communication",
    modules: ["Calls", "WhatsApp", "Campaigns", "Activity Feed"],
  },
  {
    name: "Operations",
    modules: ["Reports", "Integrations", "Web Forms"],
  },
  {
    name: "Settings",
    modules: [
      "Users", "Positions & Roles", "Team Structure", "Industry Config",
      "Project Settings", "Assignment Rules", "Lead Scoring", "Workflows",
      "Bot Identity", "Knowledge Base",
    ],
  },
];

// Modules where certain permissions don't apply
export const nonApplicablePermissions: Partial<Record<PositionModuleKey, string[]>> = {
  "Lead Import": ["edit", "delete_"],
  "Activity Feed": ["create", "edit", "delete_"],
  "Reports": ["create", "edit", "delete_"],
  "Team Structure": ["create", "delete_"],
  "Industry Config": ["create", "delete_"],
  "Project Settings": ["create", "delete_"],
  "Lead Scoring": ["create", "delete_"],
  "Bot Identity": ["create", "delete_"],
};

// ── Team / Org chart data ──
export interface TeamMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  positionId: string;
  teamName: string;
  branch: string;
  avatarInitials: string;
  leadCount: number;
  reportsToMemberId: string;
}

export const teamMembers: TeamMember[] = [
  { id: "tm-1", name: "Rajesh Kumar", email: "rajesh@crm.com", phone: "+91 98201 23456", positionId: "pos-1", teamName: "Leadership", branch: "Mumbai HQ", avatarInitials: "RK", leadCount: 34, reportsToMemberId: "" },
  { id: "tm-2", name: "Priya Sharma", email: "priya@crm.com", phone: "+91 98112 34567", positionId: "pos-2", teamName: "Leadership", branch: "Mumbai HQ", avatarInitials: "PS", leadCount: 28, reportsToMemberId: "tm-1" },
  { id: "tm-3", name: "Amit Patel", email: "amit@crm.com", phone: "+91 98765 43210", positionId: "pos-3", teamName: "Mumbai Sales", branch: "Mumbai HQ", avatarInitials: "AP", leadCount: 19, reportsToMemberId: "tm-2" },
  { id: "tm-4", name: "Sneha Rao", email: "sneha@crm.com", phone: "+91 98480 12340", positionId: "pos-3", teamName: "Pune Sales", branch: "Pune Office", avatarInitials: "SR", leadCount: 18, reportsToMemberId: "tm-2" },
  { id: "tm-5", name: "Ravi Iyer", email: "ravi@crm.com", phone: "+91 98230 11111", positionId: "pos-4", teamName: "Mumbai Sales", branch: "Mumbai HQ", avatarInitials: "RI", leadCount: 12, reportsToMemberId: "tm-3" },
  { id: "tm-6", name: "Neha Gupta", email: "neha@crm.com", phone: "+91 98450 22222", positionId: "pos-4", teamName: "Mumbai Sales", branch: "Mumbai HQ", avatarInitials: "NG", leadCount: 12, reportsToMemberId: "tm-3" },
  { id: "tm-7", name: "Karan Shah", email: "karan@crm.com", phone: "+91 98990 33333", positionId: "pos-4", teamName: "Pune Sales", branch: "Pune Office", avatarInitials: "KS", leadCount: 12, reportsToMemberId: "tm-4" },
  { id: "tm-8", name: "Vikram Desai", email: "vikram@crm.com", phone: "+91 98330 56789", positionId: "pos-5", teamName: "Mumbai Sales", branch: "Mumbai HQ", avatarInitials: "VD", leadCount: 8, reportsToMemberId: "tm-5" },
  { id: "tm-9", name: "Anjali Nair", email: "anjali@crm.com", phone: "+91 98450 98765", positionId: "pos-6", teamName: "Pune Sales", branch: "Pune Office", avatarInitials: "AN", leadCount: 8, reportsToMemberId: "tm-7" },
  { id: "tm-10", name: "Rahul Joshi", email: "rahul@crm.com", phone: "+91 98230 45678", positionId: "pos-6", teamName: "Mumbai Sales", branch: "Mumbai HQ", avatarInitials: "RJ", leadCount: 8, reportsToMemberId: "tm-5" },
  { id: "tm-11", name: "Deepak Malhotra", email: "deepak@crm.com", phone: "+91 98100 33445", positionId: "pos-5", teamName: "Pune Sales", branch: "Pune Office", avatarInitials: "DM", leadCount: 8, reportsToMemberId: "tm-7" },
  { id: "tm-12", name: "Kavita Iyer", email: "kavita@crm.com", phone: "+91 98440 11223", positionId: "pos-7", teamName: "Support", branch: "Hyderabad Branch", avatarInitials: "KI", leadCount: 0, reportsToMemberId: "tm-6" },
  { id: "tm-13", name: "Meera Chopra", email: "meera@crm.com", phone: "+91 98990 66778", positionId: "pos-8", teamName: "Support", branch: "Mumbai HQ", avatarInitials: "MC", leadCount: 0, reportsToMemberId: "tm-10" },
];